import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, message } from 'antd';
import DashboardLayout from '../components/DashboardLayout';
import { useWriteContract } from 'wagmi';
import { BigNumberish, ethers } from 'ethers';
import { usePublicClient } from 'wagmi';
import { CONTRACT_ADDRESS, CONTRACT_ABI } from '../config/contractConfig';
import '../styles/Generate-and-Accept-InviteCode.css';
import { BASE_URL } from "../utils/URL_Config";


const InviteCode: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [projectDetails, setProjectDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(null); // Track generated invite code


  const { writeContractAsync, isSuccess, isError, isPending } = useWriteContract();
  
  const publicClient = usePublicClient();
  

  // Extract query params
  const params = new URLSearchParams(location.search);
  const projectId = params.get('projectid');  // Fetch projectId from the query params

  useEffect(() => {
    if (projectId) {
      fetchProjectDetails(projectId);
    } else {
      message.error('Invalid promotion link. Redirecting to dashboard...');
      navigate('/dashboard');
    }
  }, [projectId]);

  const fetchProjectDetails = async (projectId: string) => {
    try {
      const token = localStorage.getItem('authToken'); // Get token from localStorage
      if (!token) {
        message.error('Authentication token not found. Please log in.');
        navigate('/login');
        return;
      }

      const requestBody = {
        token: token,
        chain: 'amoy',
        projects: [projectId],
      };

      const response = await fetch('https://mobius.hipple.io/api/v1/project/get_projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (data.error === 0 && data.data.projects.length > 0) {
        const project = data.data.projects[0]; // Get the first (and only) project in the response
        setProjectDetails({
          title: project.title,
          description: project.content,
          iconUrl: project.icon,
          imageUrls: JSON.parse(project.screen), // Convert the JSON string of image URLs to an array
        });
      } else {
        message.error('Failed to fetch project details.');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
      message.error('An error occurred while fetching project details.');
    }
  };

  const handleGenerateInviteCode = async () => {
    if (!projectId) return;

    try {
      setIsLoading(true);

      const tx = await writeContractAsync({
        address: CONTRACT_ADDRESS,
        abi: CONTRACT_ABI,
        functionName: 'createInviteCode',
        args: [projectId],  
      });

      if (isPending) {
        console.log('Generating invite code pending:', isPending);
      }

      if (isError) {
        console.log('Generating invite code failed:', isError);
      }

      if (isSuccess) {
        console.log('Invite code successfully generated:', isSuccess);
      }

      if (publicClient){
        const receipt = await publicClient.waitForTransactionReceipt({hash:tx});
        console.log('Transaction hash:', tx);

        const inviteCodeCreatedEvent = receipt.logs?.find((log: any) =>
          log.topics[0] === ethers.id("InviteCodeCreated(bytes32,bytes32,address)")
        );

        if(inviteCodeCreatedEvent){
          const abiCoder = new ethers.AbiCoder(); 
            
          // Decode the event data using the correct types
          const decodedLogs = abiCoder.decode(
            ['bytes32', 'bytes32', 'address'],
            inviteCodeCreatedEvent.data
          );
          const code = decodedLogs[0];
          console.log('inviteCode:', code);
          setInviteCode(code);
        }
      }

      message.success('Invite code generated successfully!');
      // You can store the invite code here or display it as needed
    } catch (error) {
      message.error('Failed to generate invite code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyInviteLink = () => {
    if (inviteCode) {
      const inviteLink = `${BASE_URL}/accept-invite?projectid=${projectId}&code=${inviteCode}`;
      navigator.clipboard.writeText(inviteLink)
        .then(() => {
          message.success('Invitation link copied to clipboard!');
        })
        .catch(() => {
          message.error('Failed to copy link. Please try again.');
        });
    }
  };

  return (
    <div className="accept-invite-container">
      {/* Upper Half */}
      <div className="accept-invite-upper">
        <img
          src={projectDetails?.iconUrl || require('../assets/images/icon.png')}
          alt="Project Icon"
          className="accept-invite-icon"
        />
        <h1 className="accept-invite-title">{projectDetails?.title || 'Loading...'}</h1>
        <Button
          type="primary"
          onClick={inviteCode ? handleCopyInviteLink : handleGenerateInviteCode}
          loading={isLoading}
          className="accept-invite-button"
        >
          {inviteCode ? 'Copy Invite Link' : 'Generate Invite Code'}
        </Button>
      </div>

      {/* Lower Half */}
      <div className="accept-invite-lower">
        {projectDetails?.imageUrls?.map((url: string, index: number) => (
          <img
            key={index}
            src={url}
            alt={`Screenshot ${index + 1}`}
            className="accept-invite-screenshot"
          />
        ))}
      </div>

      {/* Text Section */}
      <div className="accept-invite-text">
        <h2>{projectDetails?.title || 'FigFig - Create and Share'}</h2>
        <p>{projectDetails?.description || 'Loading project description...'}</p>
      </div>
    </div>
  );
};

export default InviteCode;
